'use client';

// Wraps page content in the full internal app chrome (sidebar, nav, agents,
// translator, etc.) — EXCEPT on public-facing routes that prospects/clients
// see, where we render the page bare. A prospect clicking a booking link must
// never see the internal tool's sidebar.

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import GlobalLeadsAlertBar from '@/components/GlobalLeadsAlertBar';
import SidebarNav from '@/components/SidebarNav';
import MobileHeader from '@/components/MobileHeader';
import TopBar from '@/components/TopBar';
import ConsolidatedNav from '@/components/ConsolidatedNav';
import WorkspaceBrandHost from '@/components/WorkspaceBrandHost';
import OnboardingWizard from '@/components/OnboardingWizard';
import AiHealthBanner from '@/components/AiHealthBanner';
import UiI18n from '@/components/UiI18n';
import BetaNotice from '@/components/BetaNotice';
import TrialGate from '@/components/TrialGate';
import ReadabilityAdapter from '@/components/ReadabilityAdapter';
import HiddenLinkSweeper from '@/components/HiddenLinkSweeper';

// Routes shown to people OUTSIDE the agency (prospects/clients) — render bare,
// with NONE of the agency app chrome (sidebar, nav, Cooper).
//   /book     = public booking page
//   /chat     = public hosted chatbot (also inside the embed iframe)
//   /schedule = client's no-login availability editor
//   /agent    = client's no-login live-chat console
//   /demo     = public Instant Prospect Demo (prospect's site + live AI agent)
const PUBLIC_PREFIXES = ['/book', '/chat', '/schedule', '/agent', '/demo', '/unlock'];

export default function AppShell({ children }) {
    const pathname = usePathname() || '';
    // /portal is the in-app (shelled) agency dashboard; /portal/<token> is the
    // PUBLIC client-facing portal (bare). Match only the tokenized subpath.
    const isPublic = PUBLIC_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))
        || /^\/portal\/[^/]+$/.test(pathname);
    // The AI Workspace (home) is a full-bleed 3-pane app — no page padding, no
    // consolidated tab bar, no beta banner wrapping it.
    const isWorkspace = pathname === '/';

    // When a page is loaded inside the Workspace's right-pane preview iframe it
    // carries ?embed=1 — render it BARE (no sidebar/chrome) so the preview is
    // clean. Set after mount to avoid an SSR/client hydration mismatch.
    const [isEmbed, setIsEmbed] = useState(false);
    useEffect(() => {
        try { setIsEmbed(new URLSearchParams(window.location.search).get('embed') === '1'); } catch {}
    }, []);

    // On app open (internal pages only), ping the tunnel endpoint once. If an
    // own-domain (Cloudflare named) tunnel is configured, this auto-starts it so
    // every link is live on the user's domain without any manual click.
    useEffect(() => {
        if (isPublic) return;
        fetch('/api/chatbot/tunnel').catch(() => {});
    }, [isPublic]);

    if (isPublic || isEmbed) {
        // Bare, full-width canvas — no sidebar, no internal agents, no translator.
        return (
            <div style={{ minHeight: '100vh', background: 'transparent' }}>
                <div id="global-toast"></div>
                {children}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', minHeight: '100vh' }}>
            <div id="global-toast"></div>
            <SidebarNav />
            <main style={{
                marginLeft: 'var(--sidebar-width)',
                flexGrow: 1,
                backgroundColor: 'transparent',
                minHeight: '100vh',
                minWidth: 0,
                maxWidth: '100vw',
                transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <MobileHeader />
                {isWorkspace ? (
                    // Full-bleed AI Workspace: it manages its own layout + height.
                    children
                ) : (
                    <>
                        <GlobalLeadsAlertBar />
                        <TopBar />
                        <ConsolidatedNav />
                        <div className="page-enter" key={pathname} style={{ padding: '32px 40px 48px', maxWidth: 1480, margin: '0 auto' }}>
                            <BetaNotice />
                            {children}
                        </div>
                    </>
                )}
            </main>
            <ReadabilityAdapter />
            <HiddenLinkSweeper />
            <AiHealthBanner />
            <WorkspaceBrandHost />
            <OnboardingWizard />
            <UiI18n />
            <TrialGate />
        </div>
    );
}
